import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Upload, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';

const AdminCMS = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: '', image: '', visible: true });
  const [fabrics, setFabrics] = useState([]);
  const [newFabric, setNewFabric] = useState('');

  useEffect(() => {
    // Wait for auth loading to complete before checking admin status
    if (authLoading) return;
    
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchSettings();
    fetchCategories();
    fetchFabrics();
  }, [user, authLoading]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettings({ 
        hero_image: '', 
        hero_title: 'Heritage in Every Knot', 
        hero_subtitle: 'Discover the timeless art of Bandhani tie-dye',
        story_image: '',
        story_title: '38 Years of Bandhani Excellence',
        story_text: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.post('/admin/settings', settings);
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name) {
      toast.error('Category name is required');
      return;
    }
    try {
      await api.post('/admin/categories', newCategory);
      toast.success('Category added successfully!');
      setNewCategory({ name: '', image: '', visible: true });
      fetchCategories();
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const handleUpdateCategory = async (id, updates) => {
    try {
      await api.put(`/admin/categories/${id}`, updates);
      toast.success('Category updated!');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await api.delete(`/admin/categories/${id}`);
        toast.success('Category deleted');
        fetchCategories();
      } catch (error) {
        toast.error('Failed to delete category');
      }
    }
  };

  const fetchFabrics = async () => {
    try {
      const response = await api.get('/fabrics');
      setFabrics(response.data);
    } catch (error) {
      console.error('Error fetching fabrics:', error);
    }
  };

  const handleAddFabric = async () => {
    if (!newFabric.trim()) { toast.error('Enter a fabric name'); return; }
    try {
      await api.post('/admin/fabrics', { name: newFabric.trim() });
      toast.success('Fabric type added');
      setNewFabric('');
      fetchFabrics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add fabric');
    }
  };

  const handleDeleteFabric = async (id) => {
    try {
      await api.delete(`/admin/fabrics/${id}`);
      toast.success('Fabric type deleted');
      fetchFabrics();
    } catch (error) {
      toast.error('Failed to delete fabric');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8">
      <div className="flex justify-between items-center mb-12">
        <h1 className="text-4xl md:text-5xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Website Management
        </h1>
        <Button onClick={() => navigate('/admin')} variant="outline" className="rounded-none">
          Back to Dashboard
        </Button>
      </div>

      <Tabs defaultValue="homepage" className="w-full">
        <TabsList className="mb-8">
          <TabsTrigger value="homepage">Homepage</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="homepage" className="space-y-8">
          {/* Hero Section */}
          <div className="border border-border p-8">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Hero Section (Main Banner)
            </h3>
            
            <div className="space-y-6">
              <ImageUpload
                label="Hero Image"
                value={settings?.hero_image || ''}
                onChange={(url) => setSettings({ ...settings, hero_image: url })}
              />

              <div>
                <Label className="text-xs uppercase tracking-widest">Hero Title</Label>
                <Input
                  value={settings?.hero_title || ''}
                  onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                  className="rounded-none mt-2"
                  data-testid="hero-title-input"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">Hero Subtitle</Label>
                <Input
                  value={settings?.hero_subtitle || ''}
                  onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                  className="rounded-none mt-2"
                  data-testid="hero-subtitle-input"
                />
              </div>
            </div>
          </div>

          {/* Story Section */}
          <div className="border border-border p-8 bg-secondary/30">
            <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Your Story Section
            </h3>
            <p className="text-sm text-muted-foreground mb-6">Add photos of you and your father, and tell about your 38 years legacy</p>
            
            <div className="space-y-6">
              <ImageUpload
                label="Story Image (Your & Father's Photo)"
                value={settings?.story_image || ''}
                onChange={(url) => setSettings({ ...settings, story_image: url })}
              />

              <div>
                <Label className="text-xs uppercase tracking-widest">Story Title</Label>
                <Input
                  value={settings?.story_title || ''}
                  onChange={(e) => setSettings({ ...settings, story_title: e.target.value })}
                  className="rounded-none mt-2"
                  placeholder="e.g., 38 Years of Bandhani Excellence"
                  data-testid="story-title-input"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">Your Story</Label>
                <p className="text-sm text-muted-foreground mb-2">📝 Write about your 38 years journey, family legacy, and craftsmanship</p>
                <textarea
                  value={settings?.story_text || ''}
                  onChange={(e) => setSettings({ ...settings, story_text: e.target.value })}
                  className="w-full rounded-none mt-2 p-3 border border-input bg-background min-h-[200px]"
                  placeholder="Tell your story about 38 years in Bandhani business, your father's teachings, and the family legacy..."
                  data-testid="story-text-input"
                />
              </div>
            </div>
          </div>

          {/* Process Section - How Bandhani is Made */}
          <div className="border border-border p-8 bg-accent/10">
            <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              How Bandhani is Made Section
            </h3>
            <p className="text-sm text-muted-foreground mb-6">📚 Educate customers about the Bandhani process with 4 detailed steps</p>
            
            <div className="space-y-6">
              <div>
                <Label className="text-xs uppercase tracking-widest">Section Title</Label>
                <Input
                  value={settings?.process_title || ''}
                  onChange={(e) => setSettings({ ...settings, process_title: e.target.value })}
                  className="rounded-none mt-2"
                  placeholder="e.g., The Art of Bandhani"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">Section Subtitle</Label>
                <Input
                  value={settings?.process_subtitle || ''}
                  onChange={(e) => setSettings({ ...settings, process_subtitle: e.target.value })}
                  className="rounded-none mt-2"
                  placeholder="e.g., Discover how each piece is handcrafted"
                />
              </div>

              {/* Step 1 */}
              <div className="border border-border p-6 bg-background">
                <h4 className="font-bold mb-4 text-primary">Step 1</h4>
                <div className="space-y-4">
                  <ImageUpload
                    label="Step 1 Image"
                    value={settings?.process_step1?.image || ''}
                    onChange={(url) => setSettings({ 
                      ...settings, 
                      process_step1: { ...settings?.process_step1, image: url } 
                    })}
                  />
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Step Title</Label>
                    <Input
                      value={settings?.process_step1?.title || ''}
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        process_step1: { ...settings?.process_step1, title: e.target.value } 
                      })}
                      className="rounded-none mt-2"
                      placeholder="e.g., Fabric Selection"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Description (100-150 words)</Label>
                    <textarea
                      value={settings?.process_step1?.description || ''}
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        process_step1: { ...settings?.process_step1, description: e.target.value } 
                      })}
                      className="w-full rounded-none mt-2 p-3 border border-input bg-background min-h-[120px]"
                      placeholder="Describe this step in detail..."
                    />
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="border border-border p-6 bg-background">
                <h4 className="font-bold mb-4 text-primary">Step 2</h4>
                <div className="space-y-4">
                  <ImageUpload
                    label="Step 2 Image"
                    value={settings?.process_step2?.image || ''}
                    onChange={(url) => setSettings({ 
                      ...settings, 
                      process_step2: { ...settings?.process_step2, image: url } 
                    })}
                  />
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Step Title</Label>
                    <Input
                      value={settings?.process_step2?.title || ''}
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        process_step2: { ...settings?.process_step2, title: e.target.value } 
                      })}
                      className="rounded-none mt-2"
                      placeholder="e.g., Tying the Knots"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Description (100-150 words)</Label>
                    <textarea
                      value={settings?.process_step2?.description || ''}
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        process_step2: { ...settings?.process_step2, description: e.target.value } 
                      })}
                      className="w-full rounded-none mt-2 p-3 border border-input bg-background min-h-[120px]"
                      placeholder="Describe this step in detail..."
                    />
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="border border-border p-6 bg-background">
                <h4 className="font-bold mb-4 text-primary">Step 3</h4>
                <div className="space-y-4">
                  <ImageUpload
                    label="Step 3 Image"
                    value={settings?.process_step3?.image || ''}
                    onChange={(url) => setSettings({ 
                      ...settings, 
                      process_step3: { ...settings?.process_step3, image: url } 
                    })}
                  />
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Step Title</Label>
                    <Input
                      value={settings?.process_step3?.title || ''}
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        process_step3: { ...settings?.process_step3, title: e.target.value } 
                      })}
                      className="rounded-none mt-2"
                      placeholder="e.g., Dyeing Process"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Description (100-150 words)</Label>
                    <textarea
                      value={settings?.process_step3?.description || ''}
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        process_step3: { ...settings?.process_step3, description: e.target.value } 
                      })}
                      className="w-full rounded-none mt-2 p-3 border border-input bg-background min-h-[120px]"
                      placeholder="Describe this step in detail..."
                    />
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="border border-border p-6 bg-background">
                <h4 className="font-bold mb-4 text-primary">Step 4</h4>
                <div className="space-y-4">
                  <ImageUpload
                    label="Step 4 Image"
                    value={settings?.process_step4?.image || ''}
                    onChange={(url) => setSettings({ 
                      ...settings, 
                      process_step4: { ...settings?.process_step4, image: url } 
                    })}
                  />
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Step Title</Label>
                    <Input
                      value={settings?.process_step4?.title || ''}
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        process_step4: { ...settings?.process_step4, title: e.target.value } 
                      })}
                      className="rounded-none mt-2"
                      placeholder="e.g., Final Reveal"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Description (100-150 words)</Label>
                    <textarea
                      value={settings?.process_step4?.description || ''}
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        process_step4: { ...settings?.process_step4, description: e.target.value } 
                      })}
                      className="w-full rounded-none mt-2 p-3 border border-input bg-background min-h-[120px]"
                      placeholder="Describe this step in detail..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Button onClick={handleSaveSettings} className="rounded-none w-full md:w-auto px-12 py-6" data-testid="save-settings-button">
            💾 Save All Homepage Settings
          </Button>
        </TabsContent>

        <TabsContent value="categories" className="space-y-8">
          <div className="border border-border p-8">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Add New Category
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Category Name</Label>
                <Input
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="rounded-none mt-2"
                  placeholder="e.g., Men's, Women's, Kids"
                  data-testid="new-category-name"
                />
              </div>

              <ImageUpload
                label="Category Image"
                value={newCategory.image}
                onChange={(url) => setNewCategory({ ...newCategory, image: url })}
              />

              <Button onClick={handleAddCategory} className="rounded-none" data-testid="add-category-button">
                <Upload className="mr-2 h-4 w-4" />
                Add Category
              </Button>
            </div>
          </div>

          <div className="border border-border p-8">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Manage Categories
            </h3>
            
            <div className="space-y-4">
              {categories.map((category) => (
                <div key={category.id} className="border border-border p-6">
                  <div className="space-y-4 mb-4">
                    <div>
                      <Label className="text-xs uppercase tracking-widest">Category Name</Label>
                      <Input
                        value={category.name}
                        onChange={(e) => handleUpdateCategory(category.id, { ...category, name: e.target.value })}
                        className="rounded-none mt-2"
                      />
                    </div>
                    <ImageUpload
                      label="Category Image"
                      value={category.image}
                      onChange={(url) => handleUpdateCategory(category.id, { ...category, image: url })}
                    />
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={category.visible}
                        onChange={(e) => handleUpdateCategory(category.id, { ...category, visible: e.target.checked })}
                      />
                      <span className="text-sm">Visible on website</span>
                    </label>
                    
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteCategory(category.id)}
                      className="rounded-none"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {categories.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No categories yet. Add your first category above!</p>
              )}
            </div>
          </div>

          {/* Fabric Types Management */}
          <div className="border border-border p-8">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Fabric Types
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Manage fabric types that appear in product filters and product creation form.
            </p>
            
            <div className="flex gap-3 mb-6">
              <Input
                value={newFabric}
                onChange={(e) => setNewFabric(e.target.value)}
                className="rounded-none"
                placeholder="e.g., Silk, Cotton, Georgette"
                data-testid="new-fabric-input"
                onKeyDown={(e) => e.key === 'Enter' && handleAddFabric()}
              />
              <Button onClick={handleAddFabric} className="rounded-none whitespace-nowrap" data-testid="add-fabric-button">
                Add Fabric
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {fabrics.map((fabric) => (
                <div key={fabric.id} className="flex items-center gap-2 bg-secondary px-4 py-2 rounded-full" data-testid={`fabric-tag-${fabric.id}`}>
                  <span className="text-sm">{fabric.name}</span>
                  <button
                    onClick={() => handleDeleteFabric(fabric.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
              {fabrics.length === 0 && (
                <p className="text-sm text-muted-foreground">No fabric types added yet.</p>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminCMS;